import React from 'react';
import styled from 'styled-components';
import "@fontsource/ibm-plex-mono";

const BookWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff8f4;
  border-radius: 20px;
  padding: 16px;
  width: 100%;
  max-width: 280px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const BookImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 12px;
`;

const BookTitle = styled.h3`
  margin: 0 0 6px 0;
  font-size: 18px;
  color: #a13b16;
  font-family: "IBM Plex Mono", Helvetica, monospace;
  font-weight: bold;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BookAuthor = styled.p`
  margin: 0 0 8px 0;
  color: #666;
  font-family: "IBM Plex Mono", Helvetica, monospace;
  font-style: italic;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BookInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: "IBM Plex Mono", Helvetica, monospace;
  font-size: 13px;
  color: #555;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #888;
`;

const InfoValue = styled.span`
  color: #333;
`;

const BookPrice = styled.div`
  margin-bottom: 8px;
  font-family: "IBM Plex Mono", Helvetica, monospace;
  font-weight: bold;
  color: #ae0404;
  font-size: 18px;
  text-align: center;
  padding: 8px;
  background: #ffe9e2;
  border-radius: 8px;
`;

const BookDescription = styled.p`
  font-size: 13px;
  color: #444;
  font-family: "IBM Plex Mono", Helvetica, monospace;
  margin: 0 0 12px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BookGenres = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
`;

const GenreBadge = styled.span`
  background: #ffe9e2;
  color: #a13b16;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-family: "IBM Plex Mono", Helvetica, monospace;
  font-weight: 500;
`;

const QuantityBadge = styled.div<{ low?: boolean }>`
  background: ${props => props.low ? '#ff4444' : '#4CAF50'};
  margin-top: 20px;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-family: "IBM Plex Mono", Helvetica, monospace;
  font-weight: bold;
`;

const NewBadge = styled.div`
  background: #ff4444;
  margin-top: 20px;
  color: white;
  border-radius: 50%;
  font-size: 12px;
  font-family: "IBM Plex Mono", Helvetica, monospace;
  font-weight: bold;
  padding: 4px 8px;
  position: absolute;
  top: -10px;
  left: 5px;
`;

const BookWrapperWithBadge = styled.div`
  position: relative;
  display: flex;
  align-items: start;
  justify-content: start;
  gap: 5px;
`;

interface Book {
  book_id: number;
  book_title: string;
  author_name: string;
  book_description?: string;
  book_price?: number;
  book_cover_path?: string;
  book_genres?: string[];
  book_quantity?: number;
  book_page_count?: number;
  book_publication_year?: number;
  publisher_name?: string;
  is_new: boolean;
}

interface BookCellProps {
  book: Book;
  onClick: () => void;
}

const BookCell: React.FC<BookCellProps> = ({ book, onClick }) => {
  const isLowStock = book.book_quantity !== undefined && book.book_quantity <= 5;
  
  return (
    <BookWrapperWithBadge>
      <BookWrapper onClick={onClick}>
        {book.book_cover_path && (
          <BookImage src={book.book_cover_path} alt={book.book_title} />
        )}
        {book.is_new && (
          <NewBadge>
            {'Н'}
          </NewBadge>
        )}
        <BookTitle>{book.book_title}</BookTitle>
        <BookAuthor>{book.author_name}</BookAuthor>
        
        <BookInfo>
          {book.book_publication_year && (
            <InfoRow>
              <InfoLabel>Рік:</InfoLabel>
              <InfoValue>{book.book_publication_year}</InfoValue>
            </InfoRow>
          )}
          {book.book_page_count && (
            <InfoRow>
              <InfoLabel>Сторінок:</InfoLabel>
              <InfoValue>{book.book_page_count}</InfoValue>
            </InfoRow>
          )}
          {book.publisher_name && (
            <InfoRow>
              <InfoLabel>Видавництво:</InfoLabel>
              <InfoValue style={{ 
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                maxWidth: '120px'
              }}>
                {book.publisher_name}
              </InfoValue>
            </InfoRow>
          )}
          {book.book_quantity !== undefined && (
            <InfoRow>
              <InfoLabel>В наявності:</InfoLabel>
              <InfoValue style={{ color: isLowStock ? '#ff4444' : '#4CAF50', fontWeight: 'bold' }}>
                {book.book_quantity} шт.
              </InfoValue>
            </InfoRow>
          )}
        </BookInfo>

        {book.book_price !== undefined && (
          <BookPrice>₴ {book.book_price}</BookPrice>
        )}
        
        {book.book_description && (
          <BookDescription>{book.book_description}</BookDescription>
        )}
        
        {book.book_genres && book.book_genres.length > 0 && (
          <BookGenres>
            {book.book_genres.slice(0, 3).map((genre, idx) => (
              <GenreBadge key={idx}>{genre}</GenreBadge>
            ))}
            {book.book_genres.length > 3 && (
              <GenreBadge>+{book.book_genres.length - 3}</GenreBadge>
            )}
          </BookGenres>
        )}
      </BookWrapper>
      
      {book.book_quantity !== undefined && book.book_quantity <= 5 && (
        <QuantityBadge low={book.book_quantity === 0}>
          {book.book_quantity === 0 ? 'Немає' : `${book.book_quantity} шт`}
        </QuantityBadge>
      )}
    </BookWrapperWithBadge>
  );
};

export default BookCell;